// @ts-nocheck

import React, { createContext, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import useDelayedPropVariation from '../../../hooks/useDelayedPropVariation';
import KLPoleChipDetailsContainer from '../containers/KLPoleChipDetailsContainer';
import KLPoleChipListContainer from '../containers/KLPoleChipListContainer';
import KLPoleTimerContainer from '../containers/KLPoleTimerContainer';
import KLRulesButtonContainer from '../containers/KLRulesButtonContainer';
import { KLRounds } from '../types/round';

import KLViewWrapper from './common/KLViewWrapper';

const roundId = KLRounds.POLE;

const StyleReceiver = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const RightColumn = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 20%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledRulesBtn = styled(KLRulesButtonContainer)`
  width: 100%;
`;

const KLPoleTimerWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, -2.5em);

  padding: 0 calc(20% - 0.5em);

  display: flex;
  justify-content: center;
  align-items: flex-end;

  margin-top: -2.5em;
`;

const KLPoleChipDetailsWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, #000000ff, #000000aa, #00000000);
  opacity: 0;
  padding: 1em;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  transition: none 300ms ease-in-out;
  transition-property: transform, opacity;

  ${({ $show }) => {
    if ($show) {
      return css`
        transform: translateY(-100%);
        opacity: 1;
      `;
    }
  }};
`;

type KLPoleViewContextProps = {
  onChipClick?: (chipIndex: number) => void;
  lastSelectedChipIndex?: number | null;
  chipDetailsPanelIsOpened?: boolean;
};

export const KLPoleViewContext = createContext<KLPoleViewContextProps>({
  lastSelectedChipIndex: null,
  onChipClick: undefined,
  chipDetailsPanelIsOpened: false,
});

type KLRound4Props = {
  className?: string;
  children?: React.ReactNode;
  isLocked: boolean;
};

const KLRound4 = ({ className, isLocked = false, children }: KLRound4Props) => {
  const [delayedIsLocked, prevDelayedIsLocked] = useDelayedPropVariation(isLocked);
  const [lastSelectedChipIndex, setLastSelectedChipIndex] = useState(null);
  const [chipDetailsPanelIsOpened, setChipDetailsPanelIsOpened] = useState(false);

  const onChipClick = useCallback((index) => {
    setLastSelectedChipIndex(index);
    setChipDetailsPanelIsOpened(true);
  }, []);

  const contextState = useMemo(
    () => ({
      onChipClick,
      lastSelectedChipIndex,
      chipDetailsPanelIsOpened,
    }),
    [chipDetailsPanelIsOpened, lastSelectedChipIndex, onChipClick],
  );

  const onGlobalClick = useCallback(() => {
    setChipDetailsPanelIsOpened(false);
  }, []);

  return (
    <KLPoleViewContext.Provider value={contextState}>
      <KLViewWrapper
        className={className}
        viewType={roundId}
        onClick={onGlobalClick}
        isLocked={delayedIsLocked}
        prevIsLocked={prevDelayedIsLocked}
      >
        <StyleReceiver>
          <RightColumn>
            <StyledRulesBtn roundId={roundId} />
            <KLPoleChipListContainer />
          </RightColumn>
          <KLPoleTimerWrapper>
            <KLPoleTimerContainer />
          </KLPoleTimerWrapper>
          {children}
        </StyleReceiver>

        <KLPoleChipDetailsWrapper $show={chipDetailsPanelIsOpened}>
          <KLPoleChipDetailsContainer />
        </KLPoleChipDetailsWrapper>
      </KLViewWrapper>
    </KLPoleViewContext.Provider>
  );
};

export default KLRound4;
